import { useState } from 'react';
import { useTranslation } from "react-i18next";
import { Input, Button } from "@nextui-org/react";

function DynamicForm({ inputs, onSubmit, submitLabel, isLoading = false }) {
    const { t } = useTranslation();
    const resolvedSubmitLabel = submitLabel ?? t("governmentDatabases.form.submitLabel");
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});

    // Sort inputs by index
    const sortedInputs = [...(inputs || [])].sort((a, b) => (a.index || 0) - (b.index || 0));

    const validateField = (input, value) => {
        if (input.required && (!value || value.trim() === '')) {
            return input.errorMessage || t("governmentDatabases.form.errors.required", { label: input.label });
        }

        if (value && input.validation) {
            const { pattern, minLength, maxLength, minDate, maxDate, minAge, maxAge } = input.validation;

            if (pattern) {
                try {
                    const regex = new RegExp(pattern);
                    if (!regex.test(value)) {
                        return input.errorMessage || t("governmentDatabases.form.errors.invalidFormat", { label: input.label });
                    }
                } catch {
                    // Invalid pattern from server — skip regex check rather than crash
                }
            }

            if (minLength && value.length < minLength) {
                return input.errorMessage || t("governmentDatabases.form.errors.minLength", { label: input.label, minLength });
            }

            if (maxLength && value.length > maxLength) {
                return input.errorMessage || t("governmentDatabases.form.errors.maxLength", { label: input.label, maxLength });
            }

            if (input.type === 'date' && value) {
                const dateValue = new Date(value);

                if (minDate && dateValue < new Date(minDate)) {
                    return input.errorMessage || t("governmentDatabases.form.errors.minDate", { minDate });
                }

                if (maxDate && dateValue > new Date(maxDate)) {
                    return input.errorMessage || t("governmentDatabases.form.errors.maxDate", { maxDate });
                }

                if (minAge || maxAge) {
                    const today = new Date();
                    const age = Math.floor((today - dateValue) / (365.25 * 24 * 60 * 60 * 1000));

                    if (minAge && age < minAge) {
                        return input.errorMessage || t("governmentDatabases.form.errors.minAge", { minAge });
                    }

                    if (maxAge && age > maxAge) {
                        return input.errorMessage || t("governmentDatabases.form.errors.maxAge", { maxAge });
                    }
                }
            }
        }

        return null;
    };

    const handleInputChange = (inputName, value) => {
        setFormData(prev => ({ ...prev, [inputName]: value }));

        // Clear error when user starts typing
        if (errors[inputName]) {
            setErrors(prev => ({ ...prev, [inputName]: null }));
        }
    };

    const handleSubmit = () => {
        const newErrors = {};
        let hasErrors = false;

        sortedInputs.forEach(input => {
            const error = validateField(input, formData[input.name]);
            if (error) {
                newErrors[input.name] = error;
                hasErrors = true;
            }
        });

        setErrors(newErrors);

        if (!hasErrors) {
            onSubmit(formData);
        }
    };

    const renderInput = (input) => {
        const commonProps = {
            key: input.name,
            label: input.label,
            placeholder: t("governmentDatabases.form.placeholderPrefix", { label: input.label.toLowerCase() }),
            value: formData[input.name] || '',
            onChange: (e) => handleInputChange(input.name, e.target.value),
            isRequired: input.required,
            isInvalid: !!errors[input.name],
            errorMessage: errors[input.name],
            variant: "bordered",
        };

        switch (input.type) {
            case 'date':
                return (
                    <Input
                        {...commonProps}
                        type="date"
                        max={input.validation?.maxDate}
                        min={input.validation?.minDate}
                    />
                );
            case 'number':
                return (
                    <Input
                        {...commonProps}
                        type="number"
                        minLength={input.validation?.minLength}
                        maxLength={input.validation?.maxLength}
                    />
                );
            case 'text':
            default:
                return (
                    <Input
                        {...commonProps}
                        type="text"
                        minLength={input.validation?.minLength}
                        maxLength={input.validation?.maxLength}
                    />
                );
        }
    };

    return (
        <div className="flex flex-col flex-grow">
            {/* Form Fields */}
            <div className="flex flex-col p-4 flex-grow overflow-auto">
                <div className="flex flex-col gap-4">
                    {sortedInputs.map(input => renderInput(input))}
                </div>
            </div>

            {/* Submit Button */}
            <div className="p-4">
                <Button
                    color="primary"
                    radius="sm"
                    className="w-full"
                    onClick={handleSubmit}
                    isLoading={isLoading}
                    isDisabled={isLoading}
                >
                    {resolvedSubmitLabel}
                </Button>
            </div>
        </div>
    );
}

export default DynamicForm;
